// pages/UserAdmin/crm/LembretesDialog.js
//
// Lembretes: agenda um template pra sair depois — pra uma pasta inteira ou
// pros contatos selecionados na lista.
//
// ─── Diferença pro disparo em lote (BulkTemplateDialog) ────────────────
// Lá o laço roda AQUI no navegador, um POST por contato, porque o disparo
// é agora e fechar a aba só interrompe o que a pessoa está vendo. Aqui o
// envio acontece quando ninguém está olhando, então quem envia é o
// servidor (services/lembretes, um laço que confere os vencidos de minuto
// em minuto). Esta tela só agenda e acompanha.
//
// ─── O que a tela NÃO faz ──────────────────────────────────────────────
// Não reagenda sozinha o que parou no teto do chip. Lembrete que para vira
// PARTIAL mostrando quantos faltaram, e a pessoa decide — template custa
// no cartão do cliente, e o sistema não gasta esse dinheiro por conta
// própria.
//
// ─── Fuso ──────────────────────────────────────────────────────────────
// O <input type="datetime-local"> devolve hora LOCAL sem fuso
// ("2026-09-22T14:30"). Mandar isso cru faria o backend assumir UTC e o
// envio sair 3h fora. `new Date(valor).toISOString()` resolve porque o
// construtor interpreta string sem fuso como local.
//
// ─── Blindagem contra banimento (out/26) ───────────────────────────────
// Escolhido o template, a tela pede o `/templates/preflight` (com os
// contatos, quando o alvo é a seleção) e mostra a mesma faixa do disparo
// em lote: tema proibido, conta parada pela Meta, quantos nunca
// conversaram e a caixa de opt-in, obrigatória — sem ela o servidor
// recusa com 428. O "restante hoje" do teste não vale aqui (o envio é
// outro dia); no lugar, o aviso do teto por disparo agendado.
//
// ─── Um contato só (out/26, CRM moderno) ───────────────────────────────
// Com `contato`, é o "Agendar envio" da janela do contato: abre direto no
// formulário, o alvo é só ele (CONTACTS com um id — a mesma rota e as mesmas
// travas de qualquer disparo agendado), o chip já vem o da conversa dele e,
// ao agendar, devolve o disparo pra quem abriu (`onAgendado`) e fecha. A
// lista de agendados continua sendo a da tela de Contatos.

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import api from '../../../services/Api';
import { Icon } from './icons';
import { mensagemDeErro } from './erros';
import ProtecaoDisparo, { bloqueioDoDisparo } from '../../../components/ProtecaoDisparo';

const SYNE = "'Syne', system-ui, sans-serif";

// Cores do tema do CRM (crm.css / ChatPage.css): seguem o claro e o escuro.
const tom = (k) => ({ cor: `var(--ch-${k}-d)`, bg: `var(--ch-${k}-bg)`, bd: `var(--ch-${k}-b)` });
const ROTULO_STATUS = {
  PENDING:  { texto: 'Agendado',  ...tom('blue') },
  RUNNING:  { texto: 'Enviando',  ...tom('amber') },
  DONE:     { texto: 'Enviado',   ...tom('green') },
  PARTIAL:  { texto: 'Parcial',   ...tom('amber') },
  FAILED:   { texto: 'Falhou',    ...tom('red') },
  CANCELED: { texto: 'Cancelado', cor: 'var(--cx-cap)', bg: 'var(--ch-surf)', bd: 'var(--ch-bdr)' },
};

/** Menor horário aceito pelo backend, no formato do datetime-local.
 *  5 minutos à frente: o mínimo dele é 1, mas oferecer o limite exato faz
 *  a pessoa tomar 400 enquanto termina de preencher o resto do formulário. */
function minimoLocal() {
  const d = new Date(Date.now() + 5 * 60 * 1000);
  d.setSeconds(0, 0);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
    + `T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formatarQuando(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export default function LembretesDialog({
  open,
  onClose,
  token,
  folders = [],
  selectedIds: selecionados = [],
  contato = null,          // { id, name, assigned_device_id } — modo "Agendar envio"
  onAgendado,              // (lembrete) => void, só no modo de um contato
}) {
  const umSo = Boolean(contato);
  // No modo de um contato, a "seleção" é ele — o resto do diálogo (prévia,
  // proteção, POST) segue igual.
  const selectedIds = useMemo(
    () => (contato ? [contato.id] : selecionados),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [contato?.id, selecionados],
  );
  const auth = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token],
  );

  const [lista, setLista] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [criando, setCriando] = useState(false);
  const [salvando, setSalvando] = useState(false);

  // Formulário
  const [nome, setNome] = useState('');
  const [devices, setDevices] = useState([]);
  const [deviceId, setDeviceId] = useState('');
  const [templates, setTemplates] = useState([]);
  const [carregandoTpl, setCarregandoTpl] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [alvo, setAlvo] = useState(selectedIds.length ? 'CONTACTS' : 'FOLDER');
  const [folderId, setFolderId] = useState('');
  const [quando, setQuando] = useState('');
  const [preflight, setPreflight] = useState(null);
  const [optin, setOptin] = useState(false);

  const template = useMemo(
    () => templates.find((t) => t.name === templateName) || null,
    [templates, templateName],
  );

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro('');
    try {
      const res = await api.get('/lembretes', auth);
      setLista(res.data || []);
    } catch (err) {
      setErro(mensagemDeErro(err, 'Não foi possível carregar os disparos agendados.'));
    } finally {
      setCarregando(false);
    }
  }, [auth]);

  useEffect(() => {
    if (!open) return;
    setErro('');
    setAlvo(selectedIds.length ? 'CONTACTS' : 'FOLDER');
    if (umSo) {
      setCriando(true);
      setNome(`Envio para ${contato.name || contato.phone_number || 'o contato'}`);
      setTemplateName(''); setQuando(''); setOptin(false);
      return;
    }
    carregar();
    setCriando(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Chips disponíveis — mesma fonte do disparo em lote.
  const chipDoContato = contato?.assigned_device_id;
  useEffect(() => {
    if (!open) return;
    api.get('/templates/meta-info', auth)
      .then((res) => {
        const ds = res.data?.devices || [];
        setDevices(ds);
        if (ds.length === 1) setDeviceId(ds[0].id);
        // Um contato só: o chip da conversa dele, quando ainda existe.
        else if (umSo && ds.some((d) => d.id === chipDoContato)) setDeviceId(chipDoContato);
      })
      .catch(() => setDevices([]));
  }, [open, auth, umSo, chipDoContato]);

  // Templates aprovados do chip escolhido.
  useEffect(() => {
    if (!open || !deviceId) { setTemplates([]); return; }
    setCarregandoTpl(true);
    api.get(`/templates/list/${deviceId}`, auth)
      .then((res) => {
        setTemplates((res.data?.templates || []).filter(
          (t) => (t.status || '').toUpperCase() === 'APPROVED',
        ));
      })
      .catch(() => setTemplates([]))
      .finally(() => setCarregandoTpl(false));
  }, [open, deviceId, auth]);

  // A faixa de proteção. Na pasta, a lista só existe no servidor (e muda
  // até a data), então vai sem contatos: tema, conta e opt-in valem igual.
  const idsDoAlvo = alvo === 'CONTACTS' ? selectedIds : null;
  useEffect(() => {
    setPreflight(null);
    setOptin(false);
    if (!open || !deviceId || !templateName) return undefined;
    let vivo = true;
    api.post(`/templates/preflight/${deviceId}`, {
      template_name: templateName,
      language: template?.language || 'pt_BR',
      client_ids: idsDoAlvo || [],
    }, auth)
      .then((res) => { if (vivo) setPreflight(res.data); })
      .catch(() => { if (vivo) setPreflight(null); });
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, deviceId, templateName, alvo, idsDoAlvo?.length, auth]);

  // O envio é noutro dia: o "restante hoje" do teste não se aplica.
  const protecao = preflight ? { ...preflight, teste: null } : null;
  const bloqueio = bloqueioDoDisparo(protecao);

  const podeSalvar = nome.trim() && deviceId && templateName && quando
    && (alvo === 'CONTACTS' ? selectedIds.length > 0 : !!folderId)
    && !bloqueio && optin;

  const salvar = async () => {
    setSalvando(true);
    setErro('');
    try {
      const corpo = {
        name: nome.trim(),
        device_id: deviceId,
        template_name: templateName,
        template_language: template?.language || 'pt_BR',
        // Estrutura do template pra bolha do chat ficar fiel quando sair.
        // O servidor não tem navegador pra buscá-la depois.
        template_struct: Array.isArray(template?.components)
          ? template.components : undefined,
        // Local → UTC. Ver o cabeçalho deste arquivo.
        scheduled_at: new Date(quando).toISOString(),
        optin_confirmado: optin,
      };
      if (alvo === 'FOLDER') corpo.folder_id = folderId;
      else corpo.contact_ids = selectedIds;

      const res = await api.post('/lembretes', corpo, auth);
      if (umSo) {
        onAgendado?.(res.data);
        onClose?.();
        return;
      }
      setLista((prev) => [res.data, ...prev]);
      setCriando(false);
      setNome(''); setTemplateName(''); setQuando(''); setFolderId(''); setOptin(false);
    } catch (err) {
      setErro(mensagemDeErro(err, 'Não foi possível agendar.'));
    } finally {
      setSalvando(false);
    }
  };

  const remover = async (lembrete) => {
    const pendente = lembrete.status === 'PENDING';
    const pergunta = pendente
      ? `Cancelar o disparo "${lembrete.name}"? Ele não será enviado.`
      : `Remover "${lembrete.name}" da lista?`;
    if (!window.confirm(pergunta)) return;
    try {
      await api.delete(`/lembretes/${lembrete.id}`, auth);
      if (pendente) {
        setLista((prev) => prev.map((x) => (
          x.id === lembrete.id ? { ...x, status: 'CANCELED' } : x
        )));
      } else {
        setLista((prev) => prev.filter((x) => x.id !== lembrete.id));
      }
    } catch (err) {
      setErro(mensagemDeErro(err, 'Não foi possível remover.'));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontFamily: SYNE, fontWeight: 800, pb: 1 }}>
        {/* "Disparos agendados" (out/26): "Lembretes" passou a ser, na
            Agenda, o retorno a um contato (lembrete do CRM). Este diálogo
            agenda TEMPLATE — o nome antigo confundia os dois. */}
        {umSo ? 'Agendar envio' : 'Disparos agendados'}
        <Typography sx={{ fontSize: 14, color: 'var(--cx-cap)', fontWeight: 500, mt: 0.3 }}>
          {umSo
            ? `Um template para ${contato.name || contato.phone_number}, no dia e na hora que você escolher.`
            : 'Agende um template pra sair depois, pra uma pasta ou pros contatos selecionados.'}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}

        {!criando && (
          <>
            <Button
              onClick={() => setCriando(true)}
              startIcon={<Icon.Plus size={14} />}
              variant="contained"
              disableElevation
              sx={{ mb: 2 }}
            >
              Novo disparo
            </Button>

            {carregando && (
              <Stack alignItems="center" sx={{ py: 3 }}>
                <CircularProgress size={22} />
              </Stack>
            )}

            {!carregando && lista.length === 0 && (
              <Typography sx={{ color: 'var(--cx-cap)', fontSize: 14, py: 2 }}>
                Nenhum disparo agendado ainda.
              </Typography>
            )}

            <Stack spacing={1}>
              {lista.map((l) => {
                const st = ROTULO_STATUS[l.status] || ROTULO_STATUS.PENDING;
                return (
                  <Box
                    key={l.id}
                    sx={{
                      border: '1px solid var(--ch-bdr)', borderRadius: '12px',
                      p: 1.3, display: 'flex', alignItems: 'flex-start', gap: 1,
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" spacing={0.8} alignItems="center"
                        sx={{ mb: 0.3, flexWrap: 'wrap', gap: 0.5 }}>
                        <Typography sx={{
                          fontWeight: 600, fontSize: 15,
                        }}>
                          {l.name}
                        </Typography>
                        <Chip
                          label={st.texto}
                          size="small"
                          sx={{
                            height: 22, fontSize: 12, fontWeight: 600,
                            color: st.cor, bgcolor: st.bg,
                            border: `1px solid ${st.bd}`,
                          }}
                        />
                      </Stack>
                      <Typography sx={{ fontSize: 13.5, color: 'var(--cx-cap)', fontVariantNumeric: 'tabular-nums' }}>
                        {formatarQuando(l.scheduled_at)}
                        {' · '}
                        {l.target_kind === 'FOLDER'
                          ? (l.folder_name || 'pasta removida')
                          : `${l.contact_count} contato(s)`}
                        {' · '}
                        {l.template_name}
                      </Typography>

                      {/* Resultado: só aparece depois de rodar. O número de
                          restantes é o que a pessoa usa pra decidir se
                          reagenda — por isso vem por extenso, não escondido
                          num tooltip. */}
                      {(l.status === 'DONE' || l.status === 'PARTIAL'
                        || l.status === 'FAILED') && (
                        <Typography sx={{
                          fontSize: 13, color: 'var(--ch-text2)', mt: 0.4,
                        }}>
                          {l.sent_count} enviado(s)
                          {l.failed_count > 0 && ` · ${l.failed_count} falhou(ram)`}
                          {l.remaining_count > 0
                            && ` · ${l.remaining_count} ficaram de fora (limite atingido)`}
                        </Typography>
                      )}
                      {Array.isArray(l.report) && l.report.length > 0 && (
                        <Typography sx={{
                          fontSize: 13, color: 'var(--ch-red-d)', mt: 0.3,
                        }}>
                          {l.report[0].motivo}
                          {l.report.length > 1 && ` (+${l.report.length - 1})`}
                        </Typography>
                      )}
                    </Box>

                    {l.status !== 'RUNNING' && (
                      <Button
                        size="small"
                        onClick={() => remover(l)}
                        sx={{
                          minWidth: 0, color: 'var(--cx-cap)',
                          fontSize: 13,
                          '&:hover': { color: 'var(--ch-red-d)', bgcolor: 'var(--ch-red-bg)' },
                        }}
                      >
                        {l.status === 'PENDING' ? 'Cancelar' : 'Remover'}
                      </Button>
                    )}
                  </Box>
                );
              })}
            </Stack>
          </>
        )}

        {criando && (
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <TextField
              label="Nome do disparo"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              size="small"
              fullWidth
              placeholder="Ex.: Follow-up das imobiliárias"
            />

            <TextField
              select label="Chip" value={deviceId} size="small" fullWidth
              onChange={(e) => { setDeviceId(e.target.value); setTemplateName(''); }}
            >
              {devices.map((d) => (
                <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
              ))}
            </TextField>

            <TextField
              select label="Template" value={templateName} size="small" fullWidth
              disabled={!deviceId || carregandoTpl}
              onChange={(e) => setTemplateName(e.target.value)}
              helperText={
                carregandoTpl ? 'Carregando…'
                  : (deviceId && !templates.length)
                    ? 'Nenhum template aprovado neste chip' : ' '
              }
            >
              {templates.map((t) => (
                <MenuItem key={t.name} value={t.name}>{t.name}</MenuItem>
              ))}
            </TextField>

            {!umSo && <Divider />}

            {!umSo && (
            <TextField
              select label="Enviar para" value={alvo} size="small" fullWidth
              onChange={(e) => setAlvo(e.target.value)}
            >
              <MenuItem value="FOLDER">Uma pasta inteira</MenuItem>
              <MenuItem value="CONTACTS" disabled={!selectedIds.length}>
                {selectedIds.length
                  ? `Os ${selectedIds.length} contatos selecionados`
                  : 'Contatos selecionados (nenhum marcado)'}
              </MenuItem>
            </TextField>
            )}

            {!umSo && alvo === 'FOLDER' && (
              <TextField
                select label="Pasta" value={folderId} size="small" fullWidth
                onChange={(e) => setFolderId(e.target.value)}
                helperText="Quem entrar na pasta até a data também recebe."
              >
                {folders.map((f) => (
                  <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>
                ))}
              </TextField>
            )}

            <TextField
              label="Quando enviar"
              type="datetime-local"
              value={quando}
              onChange={(e) => setQuando(e.target.value)}
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: minimoLocal(), className: 'cx-campo-nativo' }}
            />

            <Alert severity="info" sx={{ fontSize: 13 }}>
              Os dados de cada contato são lidos na hora do envio — se você
              corrigir um cadastro antes da data, vale o dado novo.
            </Alert>

            {preflight?.teste && (
              <Alert severity="warning" variant="outlined" sx={{ fontSize: 13, bgcolor: 'transparent' }}>
                No período de teste, cada disparo agendado vai até {preflight.teste.teto} contatos,
                e o envio para quando o dia chega a {preflight.teste.teto} templates.
              </Alert>
            )}

            <ProtecaoDisparo
              preflight={protecao}
              total={alvo === 'CONTACTS' ? selectedIds.length : undefined}
              optin={optin} onOptin={setOptin}
            />
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        {criando ? (
          <>
            {/* No modo de um contato não há lista pra onde voltar. */}
            <Button onClick={() => (umSo ? onClose?.() : setCriando(false))} sx={{ color: 'var(--ch-text2)' }}>
              {umSo ? 'Cancelar' : 'Voltar'}
            </Button>
            <Button
              onClick={salvar}
              disabled={!podeSalvar || salvando}
              variant="contained"
              disableElevation
            >
              {salvando ? 'Agendando…' : 'Agendar'}
            </Button>
          </>
        ) : (
          <Button onClick={onClose} sx={{ color: 'var(--ch-text2)' }}>Fechar</Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
